import { Box, Typography } from "@mui/material";
import type { DateTime } from "luxon";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { FONT_SIZE, INK, NEUTRAL, OPEN_SHIFTS, OVERTIME, SURFACE } from "@/theme/tokens";
import MetaChip, { type MetaChipCustomTone, type MetaChipTone } from "@/ui/Badges/MetaChip";
import SystemButton from "@/ui/SystemButton/SystemButton";
import { GapsRoleBadge } from "../GapsRoleBadge";
import { ROW_MARKER_SX } from "../gapsRowLayout";
import { GapsJobBadge } from "../sheets/GapsJobBadge";
import type { GapsApplicant } from "./useGapsApplicants";

type Props = {
	applicant: GapsApplicant;
	/** Hours in the home's compact form (`9a-3p`) — how a partial pickup and a double name theirs. */
	formatHours: (start: DateTime, end: DateTime) => string;
	onApprove: (applicant: GapsApplicant) => void;
	/**
	 * The request asks for more than one job, so the line says which one this person is (their job, else their role).
	 * A single-job request leaves it off, as Option A draws it: the card's own chip already names the job.
	 */
	showsJob: boolean;
	/** The last response on the card: the rail stops at its dot instead of running on. */
	isLast: boolean;
	dataTestId: string;
};

/**
 * One person who asked for a shift, and the Approve that gives it to them (P3-12), hanging off the card's rail as the
 * design's Option A draws it (APP-886).
 *
 * The line under the name prints only exceptions — the part they cover ("Covers 3p-9:30p", tagged Partial), the other
 * shift that makes it a double ("Works 11p-7a"), agency staff, the overtime it adds — so a clean candidate is just a
 * name and the eye stops where a decision gets harder.
 *
 * Approve is labelled, not a tick: it is a write that closes the shift for everyone else, so the word is the affordance.
 * It opens the confirm sheet; nothing is written from here.
 */
export const GapsApplicantLine: FC<Props> = ({ applicant, formatHours, onApprove, showsJob, isLast, dataTestId }) => {
	const { t } = useTranslation();
	const { nurse, job, partial, isAgency, otherShift, overtimeHours } = applicant;

	// What their hours are, when they are not simply the shift's: the part they cover, else the other shift they work.
	// Only a double the phone can see: for a day it has not loaded, the line claims nothing (the confirm says "not known").
	const doubleShift = otherShift.isKnown && otherShift.shift !== undefined ? otherShift.shift : undefined;
	const detail =
		partial !== null
			? { key: "partial", text: t("gaps.requests.covers", { hours: formatHours(partial.start, partial.end) }) }
			: doubleShift !== undefined
				? { key: "double", text: t("gaps.requests.works", { hours: formatHours(doubleShift.startTime, doubleShift.endTime) }) }
				: null;

	// The tags beside it: one family, the staff row's own exception chips.
	const tags: { key: string; label: string; tone?: MetaChipTone; customTone?: MetaChipCustomTone }[] = [];
	if (partial !== null) tags.push({ key: "partial-tag", label: t("gaps.requests.partialTag"), customTone: { bg: OVERTIME.BG, ink: OVERTIME.INK } });
	if (isAgency) tags.push({ key: "agency", label: t("gaps.requests.agency"), tone: "info" });
	if (overtimeHours !== undefined && overtimeHours > 0) {
		tags.push({
			key: "overtime",
			label: t("gaps.requests.overtime", { hours: Number(overtimeHours.toFixed(1)) }),
			customTone: { bg: OVERTIME.BG, ink: OVERTIME.INK },
		});
	}

	return (
		<Box data-testid={dataTestId} sx={{ display: "flex", gap: 1.25 }}>
			{/* The rail: a line down the card's responses, with a hollow dot level with each name. */}
			<Box aria-hidden sx={{ position: "relative", width: OPEN_SHIFTS.RAIL_COLUMN_PX, flex: "none" }}>
				<Box
					sx={{
						position: "absolute",
						left: (OPEN_SHIFTS.RAIL_COLUMN_PX - OPEN_SHIFTS.RAIL_WIDTH_PX) / 2,
						top: 0,
						width: OPEN_SHIFTS.RAIL_WIDTH_PX,
						bgcolor: NEUTRAL[200],
						...(isLast ? { height: OPEN_SHIFTS.RAIL_DOT_TOP_PX + OPEN_SHIFTS.RAIL_DOT_PX / 2 } : { bottom: 0 }),
					}}
				/>
				<Box
					sx={{
						position: "absolute",
						left: (OPEN_SHIFTS.RAIL_COLUMN_PX - OPEN_SHIFTS.RAIL_DOT_PX) / 2,
						top: OPEN_SHIFTS.RAIL_DOT_TOP_PX,
						width: OPEN_SHIFTS.RAIL_DOT_PX,
						height: OPEN_SHIFTS.RAIL_DOT_PX,
						// The border inside the 8px, so the dot centres on the rail it sits over.
						boxSizing: "border-box",
						borderRadius: "50%",
						bgcolor: SURFACE,
						border: `${OPEN_SHIFTS.RAIL_WIDTH_PX}px solid`,
						borderColor: "primary.light",
					}}
				/>
			</Box>
			<Box
				sx={{
					flex: 1,
					minWidth: 0,
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: 1.5,
					py: 1.75,
					...(isLast ? {} : { borderBottom: `1px solid ${NEUTRAL[150]}` }),
				}}
			>
				<Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 0.5 }}>
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
						{showsJob &&
							(job !== undefined ? (
								<GapsJobBadge group={{ jobLabel: job.displayName, isJobSection: true, jobId: job.id }} dense dataTestId={`${dataTestId}-job`} />
							) : (
								nurse !== undefined && <GapsRoleBadge role={nurse.role} size="row" />
							))}
						<Typography
							data-testid={`${dataTestId}-name`}
							sx={{ fontSize: FONT_SIZE.TITLE_SM, fontWeight: 600, color: INK.PRIMARY, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
						>
							{applicant.name}
						</Typography>
					</Box>
					{(detail !== null || tags.length > 0) && (
						<Box data-testid={`${dataTestId}-marks`} sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75 }}>
							{detail !== null && (
								<Typography component="span" data-testid={`${dataTestId}-${detail.key}`} sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.SECONDARY }}>
									{detail.text}
								</Typography>
							)}
							{tags.map((tag) => (
								<MetaChip
									key={tag.key}
									label={tag.label}
									size="small"
									tone={tag.tone}
									customTone={tag.customTone}
									sx={ROW_MARKER_SX}
									dataTestId={`${dataTestId}-${tag.key}`}
								/>
							))}
						</Box>
					)}
				</Box>
				<SystemButton
					buttonVariant="secondary"
					touchTarget
					noFullWidthMobile
					onClick={() => onApprove(applicant)}
					aria-label={t("gaps.requests.approveName", { name: applicant.name })}
					data-testid={`${dataTestId}-approve`}
					sx={{ flex: "none" }}
				>
					{t("gaps.requests.approve")}
				</SystemButton>
			</Box>
		</Box>
	);
};
